import { useState, useMemo } from 'react'
import { format, subDays } from 'date-fns'
import {
  Building2,
  Users,
  FileText,
  MessageSquare,
  Activity,
  TrendingUp,
  Filter,
  Download,
  AlertCircle,
  HardDrive
} from 'lucide-react'
import { usePlatformAnalytics, AnalyticsDateRange } from '@/hooks/useAnalytics'
import { StatCard } from './StatCard'
import { LineChart } from './LineChart'
import { PieChart } from './PieChart'
import { BarChart } from './BarChart'
import { cn } from '@/lib/utils'

interface PlatformAnalyticsProps {
  className?: string
}

export function PlatformAnalytics({ className }: PlatformAnalyticsProps) {
  const [dateRange, setDateRange] = useState<AnalyticsDateRange>({})

  // Default date range: last 30 days
  const defaultDateRange = useMemo(() => {
    const endDate = new Date()
    const startDate = subDays(endDate, 30)

    return {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd')
    }
  }, [])

  const { data: analytics, loading, error, refetch } = usePlatformAnalytics(
    Object.keys(dateRange).length > 0 ? dateRange : defaultDateRange
  )

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateRange(prev => ({ ...prev, startDate: e.target.value }))
  }

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateRange(prev => ({ ...prev, endDate: e.target.value }))
  }

  const handleResetFilter = () => {
    setDateRange({})
  }

  // Export to CSV function
  const handleExportToCSV = () => {
    if (!analytics) return

    const csvData = [
      ['Metric', 'Value'],
      ['Total Companies', analytics.totalCompanies.toString()],
      ['Total Users', analytics.totalUsers.toString()],
      ['Total Content', analytics.totalContent.toString()],
      ['Total Chats', analytics.totalChats.toString()],
      ['Active Sessions', '0'], // Placeholder - not provided by API
      [],
      ['Users by Role', 'Count'],
      ...Object.entries(analytics.usersByRole).map(([role, count]) => [role, count.toString()]),
      [],
      ['Content by Type', 'Count'],
      ...Object.entries(analytics.contentByType).map(([type, count]) => [type, count.toString()]),
      [],
      ['Top Companies', 'User Count'],
      ...analytics.topCompanies.map(company => [company.companyName, company.userCount.toString()])
    ]

    const csvContent = csvData.map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', `platform-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`)
    link.style.visibility = 'hidden'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Prepare chart data
  const companiesGrowthData = useMemo(() => {
    // Mock growth data - replace with real API data when available
    const data = []
    const endDate = new Date()
    for (let i = 29; i >= 0; i--) {
      const date = subDays(endDate, i)
      data.push({
        name: format(date, 'MMM d'),
        'Companies': Math.floor(Math.random() * 5) + 1,
        'Users': Math.floor(Math.random() * 20) + 5
      })
    }
    return data
  }, [])

  const usersByRoleData = useMemo(() => {
    if (!analytics) return []

    return Object.entries(analytics.usersByRole).map(([role, count]) => ({
      name: role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      value: count
    }))
  }, [analytics])

  const contentByTypeData = useMemo(() => {
    if (!analytics) return []

    return Object.entries(analytics.contentByType).map(([type, count]) => ({
      name: type.charAt(0).toUpperCase() + type.slice(1),
      value: count
    }))
  }, [analytics])

  // Calculate growth trends (mock data - replace with real calculations from backend)
  const growthTrends = {
    totalCompanies: { value: 8, isPositive: true },
    totalUsers: { value: 15, isPositive: true },
    totalContent: { value: 22, isPositive: true },
    totalChats: { value: 18, isPositive: true },
    activeSessions: { value: 5, isPositive: true }
  }

  // Mock storage usage data (placeholder - not provided by API)
  const storageUsage = {
    used: 125.6, // GB
    total: 500, // GB
    percentage: 25.12
  }

  // Loading state
  if (loading) {
    return (
      <div className={cn("space-y-6", className)}>
        {/* Date Range Filter Skeleton */}
        <div className="bg-card border border-border p-4 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="h-10 bg-muted rounded animate-pulse w-32"></div>
            <div className="h-9 bg-muted rounded animate-pulse w-40"></div>
            <div className="h-9 bg-muted rounded animate-pulse w-40"></div>
            <div className="h-9 bg-muted rounded animate-pulse w-32 ml-auto"></div>
          </div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-card border border-border p-6 rounded-lg animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="h-4 bg-muted rounded w-24"></div>
                <div className="h-8 bg-muted rounded w-8"></div>
              </div>
              <div className="h-8 bg-muted rounded w-16 mb-2"></div>
              <div className="h-3 bg-muted rounded w-32"></div>
            </div>
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border p-6 rounded-lg">
            <div className="h-6 bg-muted rounded w-40 mb-4 animate-pulse"></div>
            <div className="h-64 bg-muted/30 rounded animate-pulse"></div>
          </div>
          <div className="bg-card border border-border p-6 rounded-lg">
            <div className="h-6 bg-muted rounded w-40 mb-4 animate-pulse"></div>
            <div className="h-64 bg-muted/30 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Top Companies Skeleton */}
        <div className="bg-card border border-border p-6 rounded-lg">
          <div className="h-6 bg-muted rounded w-48 mb-4 animate-pulse"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <div key={i} className="h-16 bg-muted/50 rounded animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={cn("bg-destructive/10 border border-destructive/20 p-6 rounded-lg", className)}>
        <div className="flex items-start gap-4">
          <div className="p-2 bg-destructive/20 rounded-lg">
            <AlertCircle className="w-5 h-5 text-destructive" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Failed to Load Platform Analytics</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header with Date Range Filter and Export */}
      <div className="bg-card border border-border p-4 rounded-lg">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Filter className="w-4 h-4" />
            <span>Date Range:</span>
          </div>

          <div className="flex flex-wrap items-center gap-3 flex-1">
            <div className="flex items-center gap-2">
              <label htmlFor="platform-start-date" className="text-sm text-muted-foreground">
                From:
              </label>
              <input
                id="platform-start-date"
                type="date"
                value={dateRange.startDate || defaultDateRange.startDate}
                onChange={handleStartDateChange}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-foreground"
              />
            </div>

            <div className="flex items-center gap-2">
              <label htmlFor="platform-end-date" className="text-sm text-muted-foreground">
                To:
              </label>
              <input
                id="platform-end-date"
                type="date"
                value={dateRange.endDate || defaultDateRange.endDate}
                onChange={handleEndDateChange}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-foreground"
              />
            </div>

            {(dateRange.startDate || dateRange.endDate) && (
              <button
                onClick={handleResetFilter}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted"
              >
                Reset
              </button>
            )}
          </div>

          <button
            onClick={handleExportToCSV}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            Export to CSV
          </button>
        </div>
      </div>

      {/* Platform-wide Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Total Companies"
          value={analytics?.totalCompanies || 0}
          icon={Building2}
          description="Organizations on platform"
          trend={growthTrends.totalCompanies}
          className="cursor-pointer hover:border-primary/50 transition-colors"
        />

        <StatCard
          title="Total Users"
          value={analytics?.totalUsers || 0}
          icon={Users}
          description="Registered users"
          trend={growthTrends.totalUsers}
          className="cursor-pointer hover:border-primary/50 transition-colors"
        />

        <StatCard
          title="Total Content"
          value={analytics?.totalContent || 0}
          icon={FileText}
          description="Files and presentations"
          trend={growthTrends.totalContent}
          className="cursor-pointer hover:border-primary/50 transition-colors"
        />

        <StatCard
          title="Total Chats"
          value={analytics?.totalChats || 0}
          icon={MessageSquare}
          description="AI conversations"
          trend={growthTrends.totalChats}
          className="cursor-pointer hover:border-primary/50 transition-colors"
        />

        <StatCard
          title="Active Sessions"
          value="0"
          icon={Activity}
          description="Currently online"
          trend={growthTrends.activeSessions}
          className="cursor-pointer hover:border-primary/50 transition-colors"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Platform Growth Trend */}
        <div className="bg-card border border-border p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Platform Growth</h3>
            <TrendingUp className="w-5 h-5 text-muted-foreground" />
          </div>
          {companiesGrowthData.length > 0 ? (
            <LineChart
              data={companiesGrowthData}
              lines={[
                { dataKey: 'Companies', name: 'New Companies', color: 'hsl(var(--primary))' },
                { dataKey: 'Users', name: 'New Users', color: 'hsl(215, 20%, 50%)' }
              ]}
              height={280}
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No growth data available
            </div>
          )}
        </div>

        {/* User Distribution by Role */}
        <div className="bg-card border border-border p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">User Distribution by Role</h3>
            <Users className="w-5 h-5 text-muted-foreground" />
          </div>
          {usersByRoleData.length > 0 ? (
            <PieChart
              data={usersByRoleData}
              height={280}
              innerRadius={60}
              outerRadius={100}
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No user role data available
            </div>
          )}
        </div>
      </div>

      {/* Content Distribution and Storage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Content Distribution by Type */}
        <div className="bg-card border border-border p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Content Distribution by Type</h3>
            <FileText className="w-5 h-5 text-muted-foreground" />
          </div>
          {contentByTypeData.length > 0 ? (
            <BarChart
              data={contentByTypeData}
              bars={[{ dataKey: 'value', name: 'Count', color: 'hsl(var(--primary))' }]}
              height={300}
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No content type data available
            </div>
          )}
        </div>

        {/* Storage Usage */}
        <div className="bg-card border border-border p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Storage Usage</h3>
            <HardDrive className="w-5 h-5 text-muted-foreground" />
          </div>

          <div className="space-y-6">
            {/* Storage Progress Bar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Used Space</span>
                <span className="text-sm font-medium text-foreground">
                  {storageUsage.used} GB / {storageUsage.total} GB
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                <div
                  className="bg-primary h-full transition-all duration-500 ease-out"
                  style={{ width: `${storageUsage.percentage}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {storageUsage.percentage.toFixed(1)}% of storage used
              </p>
            </div>

            {/* Storage Breakdown */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-foreground">Storage Breakdown</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-primary"></div>
                    <span className="text-muted-foreground">Presentations</span>
                  </div>
                  <span className="font-medium text-foreground">45.2 GB</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-blue-600"></div>
                    <span className="text-muted-foreground">PDFs</span>
                  </div>
                  <span className="font-medium text-foreground">38.7 GB</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-green-600"></div>
                    <span className="text-muted-foreground">Videos</span>
                  </div>
                  <span className="font-medium text-foreground">28.1 GB</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-orange-500"></div>
                    <span className="text-muted-foreground">Images</span>
                  </div>
                  <span className="font-medium text-foreground">13.6 GB</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Companies by User Count */}
      <div className="bg-card border border-border p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Top Companies</h3>
            <p className="text-sm text-muted-foreground mt-1">Ranked by user count</p>
          </div>
          <Building2 className="w-5 h-5 text-muted-foreground" />
        </div>

        {analytics?.topCompanies && analytics.topCompanies.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Rank</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Company</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Company ID</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Users</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Growth</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topCompanies.map((company, index) => (
                  <tr
                    key={company.companyId}
                    className={cn(
                      "border-b border-border last:border-0 transition-colors",
                      index === 0 ? "bg-primary/5" : "hover:bg-muted/30"
                    )}
                  >
                    <td className="py-3 px-4">
                      <div className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold",
                        index === 0 ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                      )}>
                        {index + 1}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Building2 className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{company.companyName}</p>
                          {index === 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full mt-1">
                              Largest
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <code className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                        {company.companyId.slice(0, 8)}...
                      </code>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">{company.userCount}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className={cn(
                        "inline-flex items-center gap-1 text-xs font-medium",
                        Math.random() > 0.3 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                      )}>
                        <TrendingUp className="w-3 h-3" />
                        {(Math.random() * 20 + 5).toFixed(1)}%
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-muted-foreground">
            No company data available
          </div>
        )}
      </div>
    </div>
  )
}
